import { deleteImage, updateImageFilename } from '~/routes/mobile/cache/imageCache'
import { removeRecording, removeRecordingThumbnail } from '~/routes/mobile/cache/recordingCache'
import api from '~/store/api'
import { deleteObjects } from '~/store/live'
import { requestConfirm } from '~/store/ui'

const logger = console.scope('confirmActions')

// Action registry - maps serializable action descriptors to action creators Note: Some actions (like loadPreset) are registered dynamically to avoid circular imports
const actionRegistry = {
	// API mutations
	startRecording: ({ sessionId, fps, rec_source }) =>
		api.endpoints.startRecording.initiate({ sessionId, fps, rec_source }),
	deleteRecording:
		({ sessionId, recordingId }) =>
		async dispatch => {
			await dispatch(api.endpoints.deleteRecording.initiate({ sessionId, recordingId }))
			await Promise.allSettled([removeRecording(recordingId), removeRecordingThumbnail(recordingId)])
		},
	deletePresets: ({ projectId, ids }) => api.endpoints.deletePresets.initiate({ projectId, ids }),
	deleteImages: ids => api.endpoints.deleteImages.initiate(ids),
	deleteProject: id => api.endpoints.deleteProject.initiate(id),
	deleteProjects: ids => api.endpoints.deleteProjects.initiate(ids),
	deleteTemplate: id => api.endpoints.deleteTemplate.initiate(id),
	deleteAssets: ids => async dispatch => {
		const result = await dispatch(api.endpoints.deleteAssets.initiate({ ids }))
		if (result.error?.status === 409) {
			const { inUse } = result.error.data
			const assetNames = inUse.map(a => a.assetName).join(', ')
			const projectNames = [...new Set(inUse.flatMap(a => a.projects))].join(', ')
			dispatch(
				requestConfirm({
					title: 'Assets In Use',
					message: `${assetNames} ${inUse.length === 1 ? 'is' : 'are'} used in projects: ${projectNames}. Delete anyway?`,
					action: { type: 'forceDeleteAssets', params: ids },
					destructive: true,
				})
			)
		}
	},
	forceDeleteAssets: ids => api.endpoints.deleteAssets.initiate({ ids, force: true }),
	renameRecording: ({ sessionId, recordingId, inputValue }) =>
		api.endpoints.patchRecording.initiate({ sessionId, recordingId, name: inputValue }),
	renameImage:
		({ imageId, inputValue }) =>
		async () => {
			// Add .jpg extension if not present
			const filename = inputValue.endsWith('.jpg') ? inputValue : `${inputValue}.jpg`
			await updateImageFilename(imageId, filename)
			logger.info(`Renamed image ${imageId} to: ${filename}`)
		},

	// Drop downloaded copies only — the recordings stay in the cloud
	removeRecordingsFromDevice:
		({ ids }) =>
		async () => {
			await Promise.allSettled(ids.map(id => removeRecording(id)))
			logger.info(`Removed ${ids.length} recording(s) from device`)
		},

	// Batch operations
	deleteRecordings: recordings => async dispatch => {
		const ids = recordings.map(r => r.recordingId)
		await dispatch(api.endpoints.deleteRecordings.initiate(ids))
	},

	// Delete media (recordings and/or images)
	deleteMedia:
		({ recordings, images }) =>
		async dispatch => {
			// Delete images (local only)
			if (images?.length > 0) {
				const results = await Promise.allSettled(images.map(id => deleteImage(id)))
				const failures = results.filter(r => r.status === 'rejected')
				if (failures.length > 0) {
					logger.error(`Failed to delete ${failures.length} image(s)`)
				}
				logger.info(`Deleted ${images.length - failures.length} image(s)`)
			}

			// Delete recordings (server), then drop whatever copy the device still holds
			if (recordings?.length > 0) {
				const results = await Promise.allSettled(
					recordings.map(({ sessionId, recordingId }) =>
						dispatch(api.endpoints.deleteRecording.initiate({ sessionId, recordingId }))
					)
				)
				await Promise.allSettled(
					recordings.flatMap(({ recordingId }) => [
						removeRecording(recordingId),
						removeRecordingThumbnail(recordingId),
					])
				)
				const failures = results.filter(r => r.status === 'rejected')
				if (failures.length > 0) {
					logger.error(`Failed to delete ${failures.length} recording(s)`)
				}
			}
		},

	// Redux actions
	deleteObjects: ids => deleteObjects(ids),

	// Thunks registered dynamically to avoid circular imports:
	// - stopSession: registered from app/thunks.js
	// - loadPreset: registered from playback/thunks.js
}

/**
 * Resolve an action descriptor to an executable action
 * @param {Object} descriptor - { type: string, params?: any }
 * @returns {Object|Function|null} - The resolved action or null if not found
 */
export const resolveAction = descriptor => {
	if (!descriptor?.type) return null
	const creator = actionRegistry[descriptor.type]
	if (!creator) {
		logger.warn(`Unknown action type: ${descriptor.type}`)
		return null
	}
	return creator(descriptor.params)
}

/**
 * Register a new action type dynamically
 * @param {string} type - Action type name
 * @param {Function} creator - Action creator function
 */
export const registerAction = (type, creator) => {
	actionRegistry[type] = creator
}

export default actionRegistry
